// Page frame for the signed-out and gate screens: a soft navy-to-orange backdrop, the Ground Team wordmark (with the
// System / Light / Dark switch beside it), then an icon badge + title + one-line explanation above a centered rounded
// card (the iOS sign-in sheet's layout).

import type { ReactNode } from 'react';
import { ThemeSwitcher } from '@/ui';

export function AuthPage({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[var(--gt-navy)]/10 via-slate-50 to-[var(--gt-orange)]/10 px-4 py-10 dark:from-[var(--gt-navy)]/60 dark:via-slate-950 dark:to-slate-950">
      <div className="mx-auto flex w-full max-w-md flex-col gap-6">
        <div className="flex items-center gap-2 text-[var(--gt-navy)] dark:text-slate-100">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--gt-navy)] text-sm font-black text-white">GT</span>
          <span className="flex-1 text-lg font-extrabold tracking-tight whitespace-nowrap">Ground Team</span>
          <ThemeSwitcher variant="icons" />
        </div>
        {children}
      </div>
    </div>
  );
}

/** Icon, title and explanation above a card (iOS SignInHeader). */
export function AuthHeader({ icon, title, subtitle }: { icon: ReactNode; title: string; subtitle?: ReactNode }) {
  return (
    <div className="flex items-start gap-4">
      <div className="flex h-13 w-13 shrink-0 items-center justify-center rounded-2xl bg-[var(--gt-orange)] text-white shadow-sm">
        {icon}
      </div>
      <div className="min-w-0">
        <h1 className="text-2xl font-bold text-[var(--gt-navy)] dark:text-slate-50">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{subtitle}</p>}
      </div>
    </div>
  );
}

export function AuthCardBody({ children, as: As = 'div', onSubmit }: {
  children: ReactNode;
  as?: 'div' | 'form';
  onSubmit?: () => void;
}) {
  const className = 'flex flex-col gap-4 rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900';
  if (As === 'form') {
    return (
      <form
        noValidate
        className={className}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit?.();
        }}
      >
        {children}
      </form>
    );
  }
  return <div className={className}>{children}</div>;
}
